<template>
    <div class="w-full h-full">
        <slot />
        <div class="w-full h-full flex flex-col">
            <div class="flex items-center justify-center">
                <span class="w-1/2">
                    <t-input-search v-model="search" :validation="search$" @change="searchChange" autofocus
                        error-position="br">
                        <template v-slot:end>
                            <t-button-icon @click="reset" title="nettoyer le filtre" color="gray"
                                :class="{ 'invisible': !search }">
                                <XMarkIcon />
                            </t-button-icon>
                        </template>
                    </t-input-search>
                </span>
            </div>
            <TablePaginator class="flex-grow" @load="loadData" :count="dataCount" :size="25" :enabled="false"
                :overlay="false">
                <template v-slot:tbody>
                    <tr v-if="historic">
                        <td v-if="$store.settings.contactSearchLast.length" colspan="100" class="px-2">
                            <div>historique:</div>
                            <div class="grid gap-2"
                                :style="{ 'grid-template-columns': `repeat(${tilesRowSize}, minmax(0, 1fr))` }">
                                <div v-for="cId in $store.settings.contactSearchLast">
                                    <ContactSearchCard :contact="cId" />
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-else-if="dataCount === 0">
                        <td colspan="100" class="h-full">
                            <div class="h-full flex items-center justify-center">
                                aucun résultat
                            </div>
                        </td>
                    </tr>
                    <tr v-else v-for="(row, rowIdx) in tiles" :key="`tile${rowIdx}`">
                        <td v-for="(contactId, columnIdx) in row" :key="`tile${rowIdx}${columnIdx}`"
                            :style="`width: ${(1 / tilesRowSize) * 100}%;`">
                            <ContactSearchCard v-if="contactId !== undefined" :contact="data[contactId]" />
                        </td>
                    </tr>
                </template>
            </TablePaginator>
        </div>
    </div>
</template>

<script lang="ts">
import { useValidation, validators } from '@tolemac/web-components/composition'
import { XMarkIcon } from '@heroicons/vue/24/outline'

import { IContact } from '~/api/myedvenn/contact'
import { CriteriaOperator } from '~/api/tolemac/pub/filter'

import ContactSearchCard from './SearchCard.vue'
import { stringNormalize } from '~/util'

export default defineComponent({
    name: 'ContactSearch',
    components: { ContactSearchCard, XMarkIcon },
    setup() {
        const { $api, $store } = useNuxtApp()

        const data = ref<IContact[]>([])
        const dataCount = ref<number>(0)

        const historic = ref(true)

        const { refObj: search, validationObj: search$, setVal: search$setVal } = useValidation("", { minLength: validators.minLength(3), requ: validators.required })

        async function loadData(page: { limit: number, offset: number, delta: boolean, complete: ((dataLength: number) => void) }) {
            if (historic.value) historic.value = false

            try {
                const _data = await $api.contact.find({
                    limit: page.limit,
                    offset: page.offset,
                    criterias: [
                        { field: 'index', operator: CriteriaOperator.I_LIKE, value: `%${stringNormalize(search.value)}%` }
                    ],
                    orders: [{ field: 'firstname' }]
                })

                dataCount.value = _data.count
                if (page.delta) {
                    data.value.push(..._data.values)
                } else {
                    data.value = _data.values || []
                }

            } catch (e: any) {
                console.warn(e)
                dataCount.value = 0
            } finally {
                page.complete(data.value.length)
            }
        }

        const tilesRowSize = computed(() =>
            $store.display.breakpoint.lgAndUp ? 5 :
                $store.display.breakpoint.md ? 3 : 2
        )
        const tiles = ref([] as (number)[][])
        watch([() => data.value.length, tilesRowSize], ([d, c]) => {
            const rows = [] as (number)[][]

            let i = 0
            while (i < (d + c)) {
                if (i >= d) {
                    if (i % c === 0) break
                    else rows[rows.length - 1].push(undefined)
                } else {
                    if (i % c === 0) rows.push([])
                    rows[rows.length - 1].push(i)
                }
                i++;
            }
            tiles.value = rows
        }, { immediate: true })

        return {
            historic,

            tilesRowSize,
            tiles,

            data,
            dataCount,
            loadData,

            search,
            search$,
            searchChange: () => { if (search$.value.$validate()) dataCount.value = null; },

            reset() { historic.value = true; search$setVal(''); }
        }
    }
})
</script>